import { Log, delay, removeQuery, getQueryParameters } from '@artup/utilities'
import { useRouterStore, useClientStore, Notification } from '@artup/client'
import { APISession, AccountsAPI, startAPISession, clearAPISession, AccountsPush, broadcastSessionEvent, subscribeSessionEvents } from '@artup/api-client'
import { useCommonStore } from '@artup/common-ui'
import { LoginError, Guardian } from '@artup/accounts'
import { Account, Session, Preference } from '@artup/model'

export default {
  /**
   * Initializes the applet
   */
  initialize () {
    // Set up cross-window / cross-applet session synchronization
    this.initializeSessionSync()

    // Wire up route watchers to check session and permissions when required
    const { router } = useRouterStore()

    router.beforeEach(async (to) => {
      // If entered the login route, clear any existing sessions locally
      if (to.name === 'login') {
        this.clearSession({ broadcast: false })
      }

      // If route requires session, check if user is logged in
      if (to.meta.secure) {
        const { isLoggedIn } = this

        if (!isLoggedIn) {
          // Check if we can continue an existing session
          const token = loadSessionToken()
          if (token) {
            await this.authenticate({ token, broadcast: false })
          }

          // If session could not be restored, go back to `login` route
          if (!this.session) {
            // Pass the requested path in `goto` parameter, so the user can be redirected
            // back to where they wanted after the succesful login
            Log.error(`[${to.path}] Not logged in`)

            return {
              name: 'login',
              query: (to.path === '' || to.path === '/' || to.name === 'login')
                ? {}
                : { goto: to.path }
            }
          }
        }

        // Check whether the account has the required permissions.
        // Besides regular features these can be the built-in virtual permissions such as `administrator`
        if (to.meta.permissions?.length > 0) {
          const isAuthorized = Boolean(this.guardian?.canUseAll(to.meta.permissions))
          if (!isAuthorized) {
            Log.error(`[${to.path}] Not authorized`)
            return { name: 'not-authorized' }
          }
        }
      }
    })
  },

  /**
   * Attempts to authenticate the specified user and password
   * @param {String} name User name
   * @param {String} password User password
   * @param {String} token Alternative to user name and password - an existing session token
   * @param {String} reCaptchaToken reCaptcha token obtained from Google reCaptcha service during interactive login
   * @param {Boolean} silent If true, no notifications will be displayed to the user
   * @returns {Boolean} `true` if authentication succeeded
   */
  async authenticate ({ name, password, token, reCaptchaToken, silent, broadcast = true } = {}) {
    this.session = null
    this.guardian = null
    this.isLoggingIn = false
    this.isLoggingOut = false
    this.loginError = null
    this.loginMessage = null

    // Cancel any previous or pending push subscriptions / reconnect loops from prior sessions
    try {
      await AccountsPush.close(false)
    } catch {}

    const clientStore = useClientStore()
    const { environment, isDevelopmentEnvironment } = clientStore

    if ((name && password) || token) {
      this.isLoggingIn = true

      // If user credentials are specified, log in with name and password
      // otherwise try restoring the session
      const logName = name || 'session'
      if (name) {
        Log.debug(`[${logName}] Logging in ...`)
      } else {
        Log.debug(`[${logName}] Restoring session ...`)
      }

      let session, message, reason
      try {
        const result = (name && password)
          ? await AccountsAPI.login({ name, password, reCaptchaToken })
          : await AccountsAPI.relogin({ token })

        session = result?.session
        message = result?.message || 'Unknown error'
        reason = result?.reason

        if (session) {
          // Pass the session to API clients
          // Set up auth error handler to be called when API calls
          // fail with authorization errors, to capture expired
          // sessions and redirect users back to login page.
          startAPISession(session, async (error) => {
            if (AccountsAPI.isAuthenticationError(error)) {
              await this.endSession({ broadcast: false })
              await this.startSession()
            }
          })

          // Prepare permisssions guardian
          const features = await AccountsAPI.getFeatures()
          const { account, token } = session
          const guardian = new Guardian({ features, account, environment })

          const accountName = session.account?.name || name || 'session'
          Log.info(`[${accountName}] Logged in`)
          Log.debug('Account', account)
          Log.debug('Guardian', guardian)
          Log.debug('Session', { ...session, token: '***' })

          // Store session in state
          this.session = session
          this.guardian = guardian

          // Store session ID in local storage if changed, so it can survive page reloads
          if (loadSessionToken() !== token) {
            saveSessionToken(token)
          }

          // Broadcast login to other windows / applets if requested
          if (broadcast) {
            broadcastSessionEvent({ action: 'login', token, account, session })
          }

          // Subscribe to push notifications for the account in the background without blocking login
          this.subscribeToNotifications({ account }).catch(error => {
            Log.warn(`[${account.name}] Background push subscription failed: ${error.message}`)
          })
        } else {
          throw new Error(message)
        }

      } catch (error) {
        this.loginError = reason
        this.loginMessage = message || error.message

        const failedName = name || 'session'
        if (name) {
          Log.debug(`[${failedName}] Authentication failed`, message || error.message)
          if (isDevelopmentEnvironment) {
            Log.error(error)
          }
          if (reason !== LoginError.UserDisabled) {
            Notification.warning({
              message: 'Login failed',
              details: name,
              silent
            })
          }
        } else {
          Log.debug(`[${failedName}] Session expired`, message || error.message)
        }

      } finally {
        this.isLoggingIn = false
      }
    }

    return this.session != null
  },

  /**
   * Retrieves all accounts, for administrators
   * @returns {Promise<Account[]>}
   */
  async loadAccounts () {
    const accounts = await AccountsAPI.getAccounts()
    this.accounts = accounts
    return accounts
  },

  /**
   * Retrieves the specified account, for administrators
   * @param {Object} params Input parameters
   * @param {Number} params.id Account identifier
   * @returns {Promise<Account>}
   */
  async loadAccount ({ id } = {}) {
    return AccountsAPI.getAccount({ id })
  },

  /**
   * Saves the specified account together with its avatars, for administrators
   * @param {Object} params Input parameters
   * @param {Account} params.account Account to save
   * @returns {Promise<Account>} Saved account, or an object with `message` if the data was rejected
   */
  async saveAccount ({ account } = {}) {
    const result = await AccountsAPI.saveAccount({ account })

    // Keep the listing in sync
    if (result?.id) {
      const index = this.accounts.findIndex(item => item.id === result.id)
      if (index >= 0) {
        this.accounts.splice(index, 1, result)
      }
      // The administrator might have edited their own account
      if (this.session?.account?.id === result.id) {
        this.session.account = result
      }
    }

    return result
  },

  /**
   * Retrieves the security profiles which can be assigned to avatars
   * @returns {Promise<SecurityProfile[]>}
   */
  async loadSecurityProfiles () {
    if (this.securityProfiles.length === 0) {
      this.securityProfiles = await AccountsAPI.getSecurityProfiles()
    }
    return this.securityProfiles
  },

  /**
   * Clears the current session
   * @param {Object} [options]
   * @param {Boolean} [options.broadcast=false] Whether to broadcast logout event to other windows
   */
  async clearSession ({ broadcast = false } = {}) {
    // Invalidate the session on the back-end
    const { session, account } = this
    if (session && account) {
      try {
        await AccountsAPI.logout({ name: account.name, token: session.token })
        Log.debug(`[${account.name}] Logged out`)

      } catch (error) {
        // Ignore auth and network errors, probably session just expired
        if (!(AccountsAPI.isAuthenticationError(error) || AccountsAPI.isNetworkError(error))) {
          Log.exception(error)
        }
      }
    }

    // Clear session in state
    this.session = null
    this.guardian = null
    this.isLoggingIn = false
    this.isLoggingOut = false
    this.loginError = null
    this.loginMessage = null
    this.accounts = []
    this.securityProfiles = []

    // Close push connection if open
    try {
      if (AccountsPush.isOpen) {
        await AccountsPush.close(false)
      }
    } catch (error) {
      Log.warn(`Error closing push connection: ${error.message}`)
    }

    // Remove session token from local storage
    deleteSessionToken()

    // Broadcast logout to other windows / applets if requested
    if (broadcast) {
      broadcastSessionEvent({ action: 'logout' })
    }

    // Clear session in API clients
    clearAPISession()
  },

  /**
   * Initializes new user session.
   * Implement the same action in applications or applets using security,
   * so that they can initialize themselves properly once the session has started.
   * @param {Session} session Session details
   * @param {String} goto Route to follow to after the succesful login
   */
  async startSession ({ session, goto = '/' } = {}) {
    if (!session) return

    // Proceed away from login page to the home page or requested page
    // Go to home page if any of the special pages have been reloaded,
    // such as error, empty etc.
    const requestedPath = goto || (window.location.hash || '').substring(1)
    if (requestedPath) {
      const path = removeQuery(requestedPath)
      const query = getQueryParameters(requestedPath)
      const { gotoRoute } = useRouterStore()
      gotoRoute({ path, query })
    }
  },

  /**
   * Clears the current user session, redirects to login page
   * @param {Object} [options]
   * @param {Boolean} [options.broadcast=true] Whether to broadcast logout to other windows
   */
  async endSession ({ broadcast = true } = {}) {
    const { account, session } = this
    const { gotoEmpty } = useCommonStore()
    if (account && session) {
      this.isLoggingOut = true
      await gotoEmpty()
      await delay(200)
      await this.clearSession({ broadcast })
      await this.gotoLogin()
      Log.debug('SESSION ENDED', account.name)
    }
  },

  /**
   * Subscribes to account push notifications (such as logout kickout)
   * @param {Object} options
   * @param {Account} [options.account]
   */
  async subscribeToNotifications ({ account } = {}) {
    account = account || this.session?.account
    if (!account || !this.session || !APISession.hasSession) {
      try {
        await AccountsPush.close(false)
      } catch {}
      return
    }

    // Avoid duplicate subscriptions if already connected for this account
    const accountIdentifier = account.name || account.id?.toString()
    if (AccountsPush.isOpen && AccountsPush.subscribedAccountIdentifier === accountIdentifier) {
      return
    }

    try {
      const clientStore = useClientStore()
      let applet = (clientStore?.options?.id || clientStore?.options?.name || clientStore?.application?.name || '')
        .replace(/^@artup\//, '')
        .replace(/-ui$/, '')
      if (!applet && typeof window !== 'undefined' && window.location?.pathname) {
        const segment = window.location.pathname.split('/').filter(Boolean)[0]
        if (segment) applet = segment
      }
      if (!applet) applet = 'accounts'

      const pushConfig = clientStore.configuration?.push || clientStore.configuration?.services?.push

      // Look in:
      // 1. push.endpoints.accounts
      // 2. Derive from services.accounts.url or AccountsAPI.api.url (e.g. http://localhost:12200 in dev)
      // 3. Current origin as final fallback
      const accountsServiceUrl = clientStore.configuration?.services?.accounts?.url || AccountsAPI.api?.url
      let accountsEndpoint = pushConfig?.endpoints?.accounts

      if (!accountsEndpoint && accountsServiceUrl) {
        accountsEndpoint = accountsServiceUrl.replace(/^http(s?):/, (match, s) => s ? 'wss:' : 'ws:')
      }

      if (!accountsEndpoint && typeof window !== 'undefined' && window.location) {
        accountsEndpoint = `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/accounts`
      }

      if (pushConfig || accountsEndpoint) {
        if (!AccountsPush.isInitialized) {
          await AccountsPush.initialize({
            enabled: pushConfig?.enabled ?? true,
            endpoints: {
              ...(pushConfig?.endpoints || {}),
              accounts: accountsEndpoint
            }
          })
        } else if (accountsEndpoint && (!AccountsPush.configuration?.endpoints || !AccountsPush.configuration?.endpoints?.accounts)) {
          AccountsPush.configuration.endpoints = {
            ...(AccountsPush.configuration?.endpoints || {}),
            accounts: accountsEndpoint
          }
        }

        await AccountsPush.subscribeToAccountNotifications({
          account,
          applet,
          onMessage: async (data) => {
            Log.debug(`[${account.name}] Received push notification`, data)
            if (data?.action === 'logout') {
              if (this.session && !this.isLoggingOut) {
                Log.info(`[${account.name}] Logged out from another session/window, kicking out ...`)
                await this.endSession({ broadcast: false })
              }
            }
          },
          onError: (error) => {
            Log.warn(`[${account.name}] Push notification error: ${error.message}`)
          }
        })
      }
    } catch (error) {
      Log.warn(`[${account.name}] Could not subscribe to push notifications: ${error.message}`)
    }
  },

  /**
   * Adopts an active session broadcasted from another window or applet,
   * without re-authenticating or generating a new session token.
   * @param {Object} params
   * @param {Session} params.session Session to adopt
   * @returns {Promise<Boolean>}
   */
  async adoptSession ({ session } = {}) {
    if (!session?.token || !session?.account) return false

    // Cancel any previous push subscriptions
    try {
      await AccountsPush.close(false)
    } catch {}

    const clientStore = useClientStore()
    const { environment } = clientStore

    try {
      this.isLoggingIn = true

      // Re-hydrate session and account from plain serialized objects into model class instances
      const account = (session.account instanceof Account)
        ? session.account
        : new Account(session.account)

      const sessionInstance = (session instanceof Session)
        ? session
        : new Session({ ...session, account })

      sessionInstance.account = account

      // Pass the session to API clients
      startAPISession(sessionInstance, async (error) => {
        if (AccountsAPI.isAuthenticationError(error)) {
          await this.endSession({ broadcast: false })
          await this.startSession()
        }
      })

      // Prepare permissions guardian
      const features = await AccountsAPI.getFeatures()
      const guardian = new Guardian({ features, account, environment })

      // Store session in state
      this.session = sessionInstance
      this.guardian = guardian

      // Ensure local storage has the token without re-writing if identical
      if (loadSessionToken() !== sessionInstance.token) {
        saveSessionToken(sessionInstance.token)
      }

      // Subscribe to push notifications in background without blocking
      this.subscribeToNotifications({ account }).catch(error => {
        Log.warn(`[${account.name}] Background push subscription failed: ${error.message}`)
      })

      return true
    } catch (error) {
      Log.warn(`[accounts] Failed to adopt session: ${error.message}`)
      return false
    } finally {
      this.isLoggingIn = false
    }
  },

  /**
   * Initializes session synchronization across browser windows/tabs and applets
   */
  initializeSessionSync () {
    if (this._sessionSyncSubscribed) return
    this._sessionSyncSubscribed = true

    subscribeSessionEvents(async (data) => {
      if (!data?.action) return

      if (data.action === 'login') {
        const { token, session } = data
        const sessionToken = token || session?.token
        if (!sessionToken) return

        // If this window already holds this session token, nothing to do
        if (this.session?.token === sessionToken) return

        // If currently in the middle of authenticating, do not re-enter
        if (this.isLoggingIn) return

        Log.info('[accounts] Detected login in another window/applet, picking up session ...')

        let success = false
        if (session) {
          success = await this.adoptSession({ session })
        } else {
          success = await this.authenticate({ token: sessionToken, silent: true, broadcast: false })
        }

        if (success && this.session) {
          Log.info(`[accounts] Session picked up for [${this.session.account?.name}]`)

          const routerStore = useRouterStore()
          const currentRoute = routerStore?.currentRoute || routerStore?.router?.currentRoute?.value
          const isLoginRoute = currentRoute?.name === 'login' ||
            currentRoute?.path === '/accounts/login' ||
            currentRoute?.path === '/login' ||
            (typeof window !== 'undefined' && (window.location.hash.includes('/login') || window.location.hash.includes('login')))

          if (isLoginRoute) {
            const clientStore = useClientStore()
            const homeRoute = clientStore.options?.routes?.home || '/'
            const gotoRoutePath = currentRoute?.query?.goto
            const destination = gotoRoutePath || homeRoute

            Log.info(`[accounts] Moving user to home route: ${destination}`)
            await this.startSession({ session: this.session, goto: destination })
          }
        }
      } else if (data.action === 'logout') {
        if (this.session && !this.isLoggingOut) {
          Log.info('[accounts] Detected logout in another window/applet, ending session ...')
          await this.endSession({ broadcast: false })
        }
      }
    })
  },

  /**
   * Navigates to Not Authorized page
   */
  gotoNotAuthorized () {
    const { gotoRoute } = useRouterStore()
    gotoRoute({ name: 'not-authorized' })
  },

  /**
   * Navigates to Login page
   */
  gotoLogin () {
    const { gotoRoute } = useRouterStore()
    gotoRoute({ name: 'login' })
  },

  /**
   * Initiates the sign-up process
   * @param {String} name Account name
   * @param {Boolean} sendEmail If true, e-mail is sent to the account, asking to finish the signup process
   * @param {Boolean} subscribeNewsletter If true, the user subscribes to newsletters
   * @param {String} reCaptchaToken reCaptcha token obtained from Google reCaptcha service
   * @returns {Promise<Object>} Results of the signup
   */
  async signup ({ name, sendEmail, subscribeNewsletter, reCaptchaToken } = {}) {
    if (name) {
      const { token, pendingRequest, timeToExpire, account } = await AccountsAPI.signup({
        name,
        sendEmail,
        subscribeNewsletter,
        reCaptchaToken
      })
      return { token, pendingRequest, timeToExpire, account }
    }
  },

  /**
   * Resets account password
   * @param {String} name Account name
   * @param {Boolean} sendEmail If true, e-mail is sent to the account, asking to set the new password
   * @param {String} reCaptchaToken reCaptcha token obtained from Google reCaptcha service
   * @returns {Promise}
   */
  async resetPassword ({ name, sendEmail, reCaptchaToken } = {}) {
    if (name) {
      await AccountsAPI.resetPassword({ name, sendEmail, reCaptchaToken })
    }
  },

  /**
   * Changes the account password
   * @param {String} name Account name
   * @param {String} password Account password
   * @param {String} token Password reset request token, required if call is executed from unauthenticated context
   * @param {Boolean} sendEmail If true, e-mail is sent to the account, confirming the change of the password
   * @param {String} reCaptchaToken reCaptcha token obtained from Google reCaptcha service
   * @returns {Promise}
   */
  async changePassword ({ name, password, token, sendEmail, reCaptchaToken } = {}) {
    if (name) {
      await AccountsAPI.changePassword({ name, password, token, sendEmail, reCaptchaToken })
    }
  },

  /**
   * Retrieves the specified security request
   * @param {String} token Request token
   * @param {Boolean} geo Determine geographic position of the client requesting the data
   * @returns {Promise<SecurityRequest>}
   */
  async getSecurityRequest ({ token, geo } = {}) {
    if (token) {
      const request = await AccountsAPI.getSecurityRequest({ token, geo })
      return request
    }
  },

  /**
   * Activates the specified account
   * @param {String} token Activation request token
   * @param {Account} account Account details
   * @param {Boolean} sendEmail If true, e-mail is sent to the account, notifying him that the account has been activated
   * @param {String} reCaptchaToken reCaptcha token obtained from Google reCaptcha service
   * @returns {Promise<Account>} Activated account
   */
  async activateAccount ({ token, account, sendEmail, reCaptchaToken } = {}) {
    if (token && account) {
      account = await AccountsAPI.activateAccount({ token, account, sendEmail, reCaptchaToken })
      return account
    }
  },

  /**
   * Saves a single preference of a specified owner
   * @param {Preference} preference Preference to save
   * @param {Entity|Account|Object} owner Preference owner
   * @returns {Promise<Preference>} Saved preference
   */
  async savePreferenceOf ({ preference, owner } = {}) {
    if (this.isLoggedIn) {
      return AccountsAPI.savePreference({ preference, owner })
    }
  },

  /**
   * Returns all preferences of a user
   * @param {Object} [options]
   * @param {Account} [options.account] Account whose preferences to return. If not specified, current user's preferences are returned
   * @param {Account} [options.user] Alias for account
   * @returns {Promise<Dictionary<String, any>>}
   */
  async getUserPreferences ({ account, user } = {}) {
    const target = account || user || this.account
    return target?.preferences
  },

  /**
   * Returns a specified preference of a user
   * @param {Object} options
   * @param {String} options.name Name of the preference to return
   * @param {Function} [options.converter] Optional converter for the returned preference
   * @param {any} [options.defaultValue] Optional default value to return if preference is not set
   * @param {Account} [options.account] Account whose preference to return
   * @param {Account} [options.user] Alias for account
   * @returns {Promise<any>} The value of the specified user preference
   */
  async getUserPreference ({ name, defaultValue, converter, account, user } = {}) {
    const target = account || user || this.account
    if (target?.preferences) {
      const preference = target.preferences[name]
      if (preference == null) {
        return defaultValue
      } else {
        return converter ? converter(preference) : preference
      }
    } else {
      return defaultValue
    }
  },

  /**
   * Returns all preferences of an avatar
   * @param {Object} [options]
   * @param {Avatar} [options.avatar] Avatar whose preferences to return. If not specified, current avatar's preferences are returned
   * @returns {Promise<Dictionary<String, any>>} Avatar preferences
   */
  async getAvatarPreferences ({ avatar } = {}) {
    const target = avatar || this.currentAvatar
    return target?.preferences
  },

  /**
   * Returns a specified preference of an avatar
   * @param {Object} options
   * @param {String} options.name Name of the preference to return
   * @param {Function} [options.converter] Optional converter for the returned preference
   * @param {any} [options.defaultValue] Optional default value to return if preference is not set
   * @param {Avatar} [options.avatar] Avatar whose preference to return
   * @returns {Promise<any>} The value of an avatar preference
   */
  async getAvatarPreference ({ name, defaultValue, converter, avatar } = {}) {
    const target = avatar || this.currentAvatar
    const preference = target?.preferences?.[name]
    if (preference == null) {
      return defaultValue
    } else {
      return converter ? converter(preference) : preference
    }
  },

  /**
   * Returns all global preferences
   * @returns {Promise<Dictionary<String, any>>} Global preferences
   */
  async getGlobalPreferences () {
    return this.session?.preferences || this.globalPreferences
  },

  /**
   * Returns a specified global preference
   * @param {Object} options
   * @param {String} options.name Name of the preference to return
   * @param {Function} [options.converter] Optional converter for the returned preference
   * @param {any} [options.defaultValue] Optional default value to return if preference is not set
   * @returns {Promise<any>} Global preference
   */
  async getGlobalPreference ({ name, defaultValue, converter } = {}) {
    const preferences = this.session?.preferences || this.globalPreferences
    const preference = preferences?.[name]
    if (preference == null) {
      return defaultValue
    } else {
      return converter ? converter(preference) : preference
    }
  },

  /**
   * Stores a preference of the user
   * @param {Object} options
   * @param {String} options.name Preference name
   * @param {any} options.value Preference value
   * @param {Account} [options.account] Account whose preferences to store. Defaults to currently logged in account
   * @param {Account} [options.user] Alias for account
   * @returns {Promise}
   */
  async storeUserPreference ({ name, value, account, user } = {}) {
    const { isLoggedIn } = this
    const target = account || user || this.account
    if (name && isLoggedIn && target) {
      const preference = Preference.from(name, value, target)
      await AccountsAPI.savePreference({ preference, owner: target })
      if (!target.preferences) {
        target.preferences = {}
      }
      target.preferences[name] = value

      const currentUser = this.account
      if (currentUser && currentUser.id === target.id) {
        if (!currentUser.preferences) currentUser.preferences = {}
        currentUser.preferences[name] = value
      }
    }
  },

  /**
   * Stores multiple preferences of the user
   * @param {Object} options
   * @param {{name: String, value: any}[]} [options.items] List of preferences to store
   * @param {Account} [options.account] Account whose preferences to store. Defaults to currently logged in account
   * @param {Account} [options.user] Alias for account
   * @returns {Promise}
   */
  async storeUserPreferences ({ items = [], account, user } = {}) {
    const { isLoggedIn } = this
    const target = account || user || this.account
    if (items.length > 0 && isLoggedIn && target) {
      const preferences = items.map(({ name, value }) => Preference.from(name, value, target))
      await AccountsAPI.savePreferences({ preferences, owner: target })
      if (!target.preferences) {
        target.preferences = {}
      }
      for (const { name, value } of items) {
        target.preferences[name] = value
      }

      const currentUser = this.account
      if (currentUser && currentUser.id === target.id) {
        if (!currentUser.preferences) currentUser.preferences = {}
        for (const { name, value } of items) {
          currentUser.preferences[name] = value
        }
      }
    }
  },

  /**
   * Stores a preference of an avatar
   * @param {Object} options
   * @param {String} options.name Preference name
   * @param {any} options.value Preference value
   * @param {Avatar} [options.avatar] Avatar whose preference to store. Defaults to current avatar
   * @returns {Promise}
   */
  async storeAvatarPreference ({ name, value, avatar } = {}) {
    const { isLoggedIn, currentAvatar } = this
    const target = avatar || currentAvatar
    if (name && isLoggedIn && target) {
      const preference = Preference.from(name, value, target)
      await AccountsAPI.savePreference({ preference, owner: target })
      if (!target.preferences) {
        target.preferences = {}
      }
      target.preferences[name] = value

      if (currentAvatar && currentAvatar.id === target.id) {
        if (!currentAvatar.preferences) currentAvatar.preferences = {}
        currentAvatar.preferences[name] = value
      }
    }
  },

  /**
   * Stores multiple preferences of an avatar
   * @param {Object} options
   * @param {{name: String, value: any}[]} [options.items] List of preferences to store
   * @param {Avatar} [options.avatar] Avatar whose preferences to store. Defaults to current avatar
   * @returns {Promise}
   */
  async storeAvatarPreferences ({ items = [], avatar } = {}) {
    const { isLoggedIn, currentAvatar } = this
    const target = avatar || currentAvatar
    if (items.length > 0 && isLoggedIn && target) {
      const preferences = items.map(({ name, value }) => Preference.from(name, value, target))
      await AccountsAPI.savePreferences({ preferences, owner: target })
      if (!target.preferences) {
        target.preferences = {}
      }
      for (const { name, value } of items) {
        target.preferences[name] = value
      }

      if (currentAvatar && currentAvatar.id === target.id) {
        if (!currentAvatar.preferences) currentAvatar.preferences = {}
        for (const { name, value } of items) {
          currentAvatar.preferences[name] = value
        }
      }
    }
  },

  /**
   * Stores the specified global preference
   * @param {Object} options
   * @param {String} options.name Preference name
   * @param {any} options.value Preference value
   * @returns {Promise}
   */
  async storeGlobalPreference ({ name, value } = {}) {
    if (name && this.isLoggedIn) {
      const preference = Preference.from(name, value)
      await AccountsAPI.savePreference({ preference })
      if (this.session) {
        if (!this.session.preferences) {
          this.session.preferences = {}
        }
        this.session.preferences[name] = value
      }
    }
  },

  /**
   * Stores multiple global preferences
   * @param {Object} options
   * @param {{name: String, value: any}[]} [options.items] List of preferences to store
   * @returns {Promise}
   */
  async storeGlobalPreferences ({ items = [] } = {}) {
    if (items.length > 0 && this.isLoggedIn) {
      const preferences = items.map(({ name, value }) => Preference.from(name, value))
      await AccountsAPI.savePreferences({ preferences })
      if (this.session) {
        if (!this.session.preferences) {
          this.session.preferences = {}
        }
        for (const { name, value } of items) {
          this.session.preferences[name] = value
        }
      }
    }
  }
}

/**
 * Retrieves the session token from local storage
 */
function loadSessionToken () {
  return window.localStorage.getItem(APISession.tokenKey)
}

/**
 * Saves a session token in local storage
 * @param token Token to save
 */
function saveSessionToken (token) {
  window.localStorage.setItem(APISession.tokenKey, token)
}

/**
 * Removes the session token from local storage
 */
function deleteSessionToken () {
  window.localStorage.removeItem(APISession.tokenKey)
}
